import { CaretDownOutlined } from '@ant-design/icons';
import { landmineNames } from '@mm/constants/landmine-names';
import { useWhitelabelConfig } from '@mm/hooks/src/use-whitelabel-config';
import { GoogleSheetsIcon } from '@mm/icons';
import { useConnectionCRUDActions } from '@mm/state/actions/ConnectionsCRUDActions';
import { useGoogleSheetsCRUDActions } from '@mm/state/actions/GoogleSheetsCRUDActions';
import { useViewActions } from '@mm/state/actions/viewActions';
import {
  GoogleAccount,
  GoogleSheet,
  GoogleSpreadsheet,
} from '@mm/state/interfaces/googleSheet';
import {
  ConnectionKind,
  FormattedConnectionModel,
} from '@mm/state/model/ConnectionModel';
import {
  CaptureActionEnum,
  SuppressionSettingsCSVModel,
} from '@mm/state/model/SuppressionSettingsModel';
import { useConnectionsTableActions } from '@mm/state/modules/connectionsTableState/actions';
import { GoogleSheetsConnections } from '@mm/state/modules/connectionsTableState/selectors';
import { selectGoogleAccounts } from '@mm/state/selectors/googleSheetSelectors';
import { activeFacebookPageIdSelector } from '@mm/state/selectors/viewSelectors';
import { OAuthPopupActionButton } from '@mm/stateful-components/OAuth/OAuthPopupActionButton';
import { Collapse, CollapseProps, ConfigProvider, Table } from 'antd';
import { ColumnType } from 'antd/es/table/interface';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Menu } from '../../../../Automations/AutomationEdit/DialogueEditor/NodeTypes/elements/NodeMenu/NodeMenu';
import { BaseIntegration } from '../../../BaseIntegration/BaseIntegration';
import { BaseIntegrationEmptyState } from '../../../BaseIntegration/BaseIntegrationEmptyState';
import { BaseIntegrationHeader } from '../../../BaseIntegration/BaseIntegrationHeader';
import { IntegrationErrors } from '../../../constants';
import { ReactComponent as AddGoogleAccountIcon } from '../../../icons/add-google-sheets.svg';
import '../../IntegrationBaseComponent.scss';
import './GoogleSheetsIntegration.scss';
import { ReactComponent as TableIcon } from '../../../icons/table.svg';
import { GoogleSheetsHeader } from './GoogleSheetsHeader';
import { ContactSuppressionHeader } from '../ContactSuppressions/ContactSuppressionHeader';
import { ContactsSuppresstionsLists } from '../ContactSuppressions/ContactsSuppresstionsLists';
import { useSuppressions } from '../ContactSuppressions/use-suppressions';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import {
  errorToast,
  successToast,
} from '../../../../../../../../common/helpers/toast';
import SuppressionSettingsController from '@mm/state/controllers/SuppressionSettingsController';
import classNames from 'classnames';

type Props = {
  isAccountConnected: boolean;
  onDisconnect: (id: number) => void;
  spreadsheetCount: number;
  columns: ColumnType<FormattedConnectionModel>[];
  googleSheetsConnections: GoogleSheetsConnections[];
  name?: string;
};

export const GoogleSheets = (props: Props) => {
  const {
    isAccountConnected,
    onDisconnect,
    spreadsheetCount,
    columns,
    googleSheetsConnections,
    name,
  } = props;

  const accounts = useSelector(selectGoogleAccounts);
  const facebookPageId = useSelector(activeFacebookPageIdSelector);
  const { getOauthUrl, getAccounts } = useGoogleSheetsCRUDActions();
  const { setActiveIntegration } = useConnectionsTableActions();
  const { create } = useConnectionCRUDActions();
  const { showSlideoutMenu } = useViewActions();
  const { companyName = 'Customers.ai' } = useWhitelabelConfig();
  const { fetchCsvFiles, mapCSVFiles } = useSuppressions();
  const { closeModal, openGoogleSpreadSheetModal } = useModalActions();
  const [haveErrors, setHaveErrors] = useState(false);
  const [uploadedCSVFiles, setUploadedCSVFiles] = useState<
    SuppressionSettingsCSVModel[] | undefined
  >([]);

  const csvs = mapCSVFiles(uploadedCSVFiles);
  const csvFiles = [...csvs];
  const csvSuppressionLists = csvFiles.filter(
    item =>
      item.type === 'google_sheet' &&
      item.captureAction === CaptureActionEnum.do_not_send,
  );

  const csvUnsubscribeLists = csvFiles.filter(
    item =>
      item.type === 'google_sheet' &&
      item.captureAction === CaptureActionEnum.do_not_capture,
  );

  const addNewConnection = async (googleAccount?: GoogleAccount) => {
    const connection = await create(facebookPageId, {
      name: 'Connection',
      description: '',
      kind: ConnectionKind.WIDGET,
      connectionKind: 'google_spreadsheet',
      ...(googleAccount && !googleAccount.readOnly
        ? {
            googleAccountId: googleAccount.id,
            googleAccountEmail: googleAccount.email,
          }
        : {}),
    });

    if (connection) {
      showSlideoutMenu('googleSheetIntegrationEditor', {
        widgetId: connection.id,
      });
    }
  };

  const onClickAddNew = (updatedGoogleAccounts?: GoogleAccount[]) => {
    const sortedAccounts = updatedGoogleAccounts?.sort((a, b) =>
      (a.updatedAt || '').localeCompare(b.updatedAt || ''),
    );

    addNewConnection(sortedAccounts?.[0]);
    setActiveIntegration(name);
  };

  const retrieveCSV = async (captureAction: CaptureActionEnum) => {
    const files = await fetchCsvFiles(captureAction, 'google_sheet');
    setUploadedCSVFiles(files.csvs);
  };

  const handleGoogleSpreadSheetAdd = async (
    {
      account,
      sheet,
      spreadsheet,
      lookupColumnName,
    }: {
      account: GoogleAccount;
      spreadsheet: GoogleSpreadsheet;
      sheet: GoogleSheet;
      lookupColumnName?: string;
    },
    captureAction: CaptureActionEnum,
  ) => {
    const defaultFileName = `${String(spreadsheet.name)} - ${String(
      sheet.title,
    )}`;
    const fileName = lookupColumnName
      ? `${defaultFileName} - ${lookupColumnName}`
      : defaultFileName;
    const payload = {
      fileName,
      sourceSize: 1,
      googleAccountId: account.id,
      googleSpreadsheetId: spreadsheet.id,
      googleSpreadsheetTitle: String(spreadsheet.name),
      googleSheetId: sheet.id || -1,
      googleSheetTitle: String(sheet.title),
      googleLookupColumn: lookupColumnName,
      captureAction,
    };

    try {
      await SuppressionSettingsController.createCSV(facebookPageId, payload);

      successToast(`Connected: "${fileName}"`);
      await retrieveCSV(captureAction);
      closeModal();
    } catch (err) {
      errorToast(
        `Error connecting: ${err?.message ? err.message.toString() : ''}`,
        'top-right',
      );
    }
  };

  const handleGoogleSheetsClick = (captureAction: CaptureActionEnum) => {
    openGoogleSpreadSheetModal({
      onReject: () => closeModal(),
      onSubmit: ({ account, spreadsheet, sheet, lookupColumnName }) => {
        if (!facebookPageId) return Promise.resolve();
        return handleGoogleSpreadSheetAdd?.(
          {
            account,
            spreadsheet,
            sheet,
            lookupColumnName,
          },
          captureAction,
        );
      },
    });
  };

  useEffect(() => {
    const errorsString = googleSheetsConnections
      .map(connection =>
        connection.connections.map(webhook =>
          webhook.webhookErrors?.map(error => error.errorDetails),
        ),
      )
      .flat()
      .join('');
    setHaveErrors(
      IntegrationErrors.some(error =>
        errorsString.toLowerCase().includes(error),
      ),
    );
  }, [googleSheetsConnections]);

  const headerComponent = (
    <GoogleSheetsHeader
      isAccountConnected={isAccountConnected}
      spreadsheetCount={spreadsheetCount}
      onClickAddNew={onClickAddNew}
    />
  );

  const sectionProps = {
    landmineName: landmineNames.INTEGRATIONS_NATIVE_INTEGRATIONS,
    featureFlag: 'nativeIntegrations',
  };

  useEffect(() => {
    retrieveCSV(CaptureActionEnum.do_not_send);
    retrieveCSV(CaptureActionEnum.do_not_capture);
  }, []);

  const renderTable = (dataSource: any[]) => (
    <ConfigProvider
      theme={{
        components: {
          Table: {
            headerBg: 'white',
            borderColor: '#D9D9D9',
            cellPaddingBlockSM: 3,
          },
        },
      }}
    >
      <div className="integration-panel--ant-table">
        <Table
          dataSource={dataSource}
          columns={columns}
          pagination={false}
          size="middle"
          bordered
        />
      </div>
    </ConfigProvider>
  );

  const items: CollapseProps['items'] = googleSheetsConnections
    .filter(c => c.connections.length || c.accountEmail)
    .map(c => ({
      key: c.accountId?.toString(),
      label: (
        <span className="integrations-grid___account-name">
          {c.accountEmail ? c.accountEmail : 'Not Assigned'}{' '}
        </span>
      ),
      children: renderTable(c.connections),
      extra: (
        <Menu
          className="google_integrations__menu"
          menu={[
            {
              children: (
                <div
                  className={classNames('google_integrations__menu-action', {
                    'google_integrations__menu-action--disabled': accounts.find(
                      acc => acc.id === c.accountId && acc.readOnly,
                    ),
                  })}
                >
                  Disconnect
                </div>
              ),
              onClick: () => {
                if (
                  accounts.find(acc => acc.id === c.accountId && !acc.readOnly)
                ) {
                  onDisconnect(
                    Number(accounts.find(acc => acc.id === c.accountId)?.id),
                  );
                }
              },
            },
          ]}
          direction="bottom"
          useThreeDotButton
        />
      ),
    }));

  return (
    <BaseIntegration
      openOnMount
      titleComponent={
        <BaseIntegrationHeader
          title="Google Sheets"
          subtitle={`Automatically send customers data from ${companyName} to Google Sheets`}
          icon={<GoogleSheetsIcon />}
          css={{ icon: 'integrations__header-icon-googlesheets' }}
          haveErrors={
            haveErrors || csvFiles.some(csv => csv?.processed === false)
          }
          {...sectionProps}
        />
      }
      integrationType="google_spreadsheet"
      emptyStateConfig={{
        button: accounts.length ? (
          'Add a Google Sheets Connection'
        ) : (
          <OAuthPopupActionButton
            fetchOauthPopupUrl={() => getOauthUrl('post_message')}
            oauthSuccessMessageType="googleAccountConnected"
            onRefreshNeeded={async () => {
              const accounts = await getAccounts(facebookPageId);
              if (accounts?.length) {
                onClickAddNew(accounts);
              }
            }}
            spinnerProps={{ size: 16, width: 32 }}
            buttonProps={{
              css: { container: 'integrations__empty-state-button' },
            }}
            {...sectionProps}
          >
            Add a Google Sheets Account
          </OAuthPopupActionButton>
        ),
        onClick: () => {
          onClickAddNew();
        },
        icon: <AddGoogleAccountIcon />,
        ...sectionProps,
      }}
      showEmptyState={!googleSheetsConnections.length || !accounts.length}
    >
      <div className="google_integrations__container">
        <div className="google_integrations__label">Sources</div>
        <div className="google_integrations__content_wrapper">
          <div className="google_integrations__content">
            <ContactSuppressionHeader
              isSuppression
              withNote
              count={csvSuppressionLists?.length}
              onClick={() =>
                handleGoogleSheetsClick(CaptureActionEnum.do_not_send)
              }
            />

            {csvSuppressionLists.length ? (
              <ContactsSuppresstionsLists
                data={csvSuppressionLists}
                onSuccess={() => retrieveCSV(CaptureActionEnum.do_not_send)}
              />
            ) : (
              <BaseIntegrationEmptyState
                button="Add a Google Sheets Suppression List"
                onClick={() =>
                  handleGoogleSheetsClick(CaptureActionEnum.do_not_send)
                }
                icon={<TableIcon />}
                {...sectionProps}
              />
            )}
          </div>
          <div className="google_integrations__content">
            <ContactSuppressionHeader
              isSuppression={false}
              withNote
              count={csvUnsubscribeLists?.length}
              onClick={() =>
                handleGoogleSheetsClick(CaptureActionEnum.do_not_capture)
              }
            />

            {csvUnsubscribeLists.length ? (
              <ContactsSuppresstionsLists
                data={csvUnsubscribeLists}
                onSuccess={() => retrieveCSV(CaptureActionEnum.do_not_capture)}
              />
            ) : (
              <BaseIntegrationEmptyState
                button="Add a Google Sheets Unsubscribe List"
                onClick={() =>
                  handleGoogleSheetsClick(CaptureActionEnum.do_not_capture)
                }
                icon={<TableIcon />}
                {...sectionProps}
              />
            )}
          </div>
        </div>
      </div>
      <div className="google_integrations__container" style={{ marginTop: 16 }}>
        <div className="google_integrations__label">Destinations</div>
        <div className="google_integrations__content">
          <div className="google_integrations__header">
            <div className="google_integrations__header_title">
              CRM connections
              <div className="google_integrations__header_subtitle">
                Send leads directly to your CRM
              </div>
            </div>
            {headerComponent}
          </div>
        </div>
        <Collapse
          items={items}
          className="integrations__collapse"
          expandIcon={({ isActive }) => (
            <CaretDownOutlined
              style={{ fontSize: 20 }}
              rotate={isActive ? 180 : 0}
            />
          )}
        />
      </div>
    </BaseIntegration>
  );
};
